import { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';

interface VisitorLog {
  id: string;
  visitor_id: string;
  email: string | null;
  visit_count: number;
  first_visit: string;
  last_visit: string;
  user_agent: string | null;
}

interface RegisteredUser {
  id: string;
  email: string;
  created_at: string;
  last_sign_in_at: string | null;
  raw_user_meta_data: {
    full_name?: string;
    name?: string;
  } | null;
  visit_count?: number;
  last_visit?: string;
  first_visit?: string;
}

interface VisitorLogsModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function VisitorLogsModal({ isOpen, onClose }: VisitorLogsModalProps) {
  const [logs, setLogs] = useState<VisitorLog[]>([]);
  const [registeredUsers, setRegisteredUsers] = useState<RegisteredUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filterType, setFilterType] = useState<'all' | 'authenticated' | 'anonymous'>('all');
  const [sortBy, setSortBy] = useState<'recent' | 'frequent'>('recent');
  const [totalVisitors, setTotalVisitors] = useState(0);
  const [totalVisits, setTotalVisits] = useState(0);
  const [authenticatedCount, setAuthenticatedCount] = useState(0);
  const [registeredCount, setRegisteredCount] = useState(0);
  const [activeTab, setActiveTab] = useState<'visitors' | 'users'>('visitors');

  useEffect(() => {
    if (isOpen) {
      fetchLogs();
    }
  }, [isOpen]);

  const fetchLogs = async () => {
    try {
      setLoading(true);
      setError('');

      if (!supabase) {
        throw new Error('Supabase client not initialized');
      }

      // Get the current session
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      
      if (sessionError) {
        console.error('Session error:', sessionError);
        throw new Error('Failed to get session. Please sign in again.');
      }

      if (!session) {
        throw new Error('You must be signed in to view analytics. Please sign in first.');
      }

      const supabaseUrl = import.meta.env.VITE_PUBLIC_SUPABASE_URL;
      const response = await fetch(
        `${supabaseUrl}/functions/v1/get-visitor-logs`,
        {
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${session.access_token}`,
          },
        }
      );

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Response error:', errorText);
        
        if (response.status === 401) {
          throw new Error('Authentication failed. Please sign in again.');
        }
        
        throw new Error(`Failed to fetch visitor logs: ${response.status}`);
      }

      const result = await response.json();

      if (result.error) {
        throw new Error(result.error);
      }

      const logsData = result.logs || [];
      const stats = result.statistics || {};
      const users = result.registeredUsers || [];

      setLogs(logsData);
      setTotalVisitors(stats.totalVisitors || logsData.length);
      setTotalVisits(stats.totalVisits || 0);
      setAuthenticatedCount(stats.authenticatedVisitors || 0);
      setRegisteredCount(stats.registeredCount || 0);
      setRegisteredUsers(users);

    } catch (error: any) {
      console.error('Error fetching logs:', error);
      setError(error.message || 'Failed to load visitor logs');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getBrowserInfo = (userAgent: string | null) => {
    if (!userAgent) return 'Unknown';
    
    if (userAgent.includes('Chrome')) return 'Chrome';
    if (userAgent.includes('Firefox')) return 'Firefox';
    if (userAgent.includes('Safari')) return 'Safari';
    if (userAgent.includes('Edge')) return 'Edge';
    return 'Other';
  };

  const filteredAndSortedLogs = logs.filter(log => {
    if (filterType === 'authenticated') return log.email;
    if (filterType === 'anonymous') return !log.email;
    return true;
  }).sort((a, b) => {
    if (sortBy === 'frequent') {
      return b.visit_count - a.visit_count;
    }
    return new Date(b.last_visit).getTime() - new Date(a.last_visit).getTime();
  });

  const exportToCSV = () => {
    if (activeTab === 'visitors') {
      // Export visitor logs
      const headers = ['Visitor ID', 'Email', 'Visit Count', 'First Visit', 'Last Visit', 'Browser'];
      const csvData = filteredAndSortedLogs.map(log => [
        log.visitor_id,
        log.email || 'Anonymous',
        log.visit_count,
        formatDate(log.first_visit),
        formatDate(log.last_visit),
        log.user_agent || 'Unknown'
      ]);

      const csvContent = [
        headers.join(','),
        ...csvData.map(row => row.map(cell => `"${cell}"`).join(','))
      ].join('\n');

      const blob = new Blob([csvContent], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `visitor-logs-${new Date().toISOString().split('T')[0]}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
    } else {
      // Export registered users
      const headers = ['Email', 'Name', 'Registered Date', 'Last Sign In', 'Visit Count', 'Last Visit'];
      const csvData = registeredUsers.map(user => [
        user.email,
        user.raw_user_meta_data?.full_name || user.raw_user_meta_data?.name || 'N/A',
        formatDate(user.created_at),
        user.last_sign_in_at ? formatDate(user.last_sign_in_at) : 'Never',
        user.visit_count || 0,
        user.last_visit ? formatDate(user.last_visit) : 'Never'
      ]);

      const csvContent = [
        headers.join(','),
        ...csvData.map(row => row.map(cell => `"${cell}"`).join(','))
      ].join('\n');

      const blob = new Blob([csvContent], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `registered-users-${new Date().toISOString().split('T')[0]}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
    }
  };

  return (
    <>
      {isOpen && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              onClose();
            }
          }}
        >
          <div className="bg-white rounded-xl max-w-6xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            {/* Header */}
            <div className="p-6 border-b border-gray-200 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 flex items-center justify-center bg-blue-100 rounded-lg">
                  <i className="ri-file-list-3-line text-2xl text-blue-600"></i>
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-gray-900">Analytics Dashboard</h2>
                  <p className="text-sm text-gray-600">Track visitors and registered users</p>
                </div>
              </div>
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-600 cursor-pointer transition-colors"
              >
                <i className="ri-close-line text-2xl"></i>
              </button>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto p-6">
              {loading ? (
                <div className="flex items-center justify-center py-20">
                  <div className="text-center">
                    <i className="ri-loader-4-line text-4xl text-blue-600 animate-spin mb-4"></i>
                    <p className="text-gray-600">Loading analytics...</p>
                  </div>
                </div>
              ) : error ? (
                <div className="text-center py-20">
                  <i className="ri-error-warning-line text-5xl text-red-500 mb-4"></i>
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">Error Loading Data</h3>
                  <p className="text-gray-600 mb-4">{error}</p>
                  <button
                    onClick={fetchLogs}
                    className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    Try Again
                  </button>
                </div>
              ) : (
                <>
                  {/* Statistics Cards */}
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                    <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl p-6 text-white">
                      <div className="flex items-center justify-between mb-2">
                        <i className="ri-user-line text-3xl opacity-80"></i>
                        <span className="text-2xl font-bold">{totalVisitors}</span>
                      </div>
                      <p className="text-sm opacity-90">Total Visitors</p>
                    </div>

                    <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl p-6 text-white">
                      <div className="flex items-center justify-between mb-2">
                        <i className="ri-eye-line text-3xl opacity-80"></i>
                        <span className="text-2xl font-bold">{totalVisits}</span>
                      </div>
                      <p className="text-sm opacity-90">Total Visits</p>
                    </div>

                    <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-xl p-6 text-white">
                      <div className="flex items-center justify-between mb-2">
                        <i className="ri-user-follow-line text-3xl opacity-80"></i>
                        <span className="text-2xl font-bold">{authenticatedCount}</span>
                      </div>
                      <p className="text-sm opacity-90">Authenticated Users</p>
                    </div>

                    <div className="bg-gradient-to-br from-orange-500 to-orange-600 rounded-xl p-6 text-white">
                      <div className="flex items-center justify-between mb-2">
                        <i className="ri-user-add-line text-3xl opacity-80"></i>
                        <span className="text-2xl font-bold">{registeredCount}</span>
                      </div>
                      <p className="text-sm opacity-90">Registered Users</p>
                    </div>
                  </div>

                  {/* Tabs */}
                  <div className="flex gap-2 mb-6 border-b border-gray-200">
                    <button
                      onClick={() => setActiveTab('visitors')}
                      className={`px-4 py-2 font-medium transition-colors cursor-pointer whitespace-nowrap ${
                        activeTab === 'visitors'
                          ? 'text-blue-600 border-b-2 border-blue-600'
                          : 'text-gray-600 hover:text-gray-900'
                      }`}
                    >
                      <i className="ri-eye-line mr-2"></i>
                      Visitor Logs
                    </button>
                    <button
                      onClick={() => setActiveTab('users')}
                      className={`px-4 py-2 font-medium transition-colors cursor-pointer whitespace-nowrap ${
                        activeTab === 'users'
                          ? 'text-blue-600 border-b-2 border-blue-600'
                          : 'text-gray-600 hover:text-gray-900'
                      }`}
                    >
                      <i className="ri-user-add-line mr-2"></i>
                      Registered Users ({registeredCount})
                    </button>
                  </div>

                  {activeTab === 'visitors' ? (
                    <>
                      {/* Filters and Controls */}
                      <div className="flex flex-wrap gap-4 mb-6">
                        <div className="flex gap-2">
                          <button
                            onClick={() => setFilterType('all')}
                            className={`px-4 py-2 rounded-lg transition-colors cursor-pointer whitespace-nowrap ${
                              filterType === 'all'
                                ? 'bg-blue-600 text-white'
                                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                            }`}
                          >
                            All Visitors
                          </button>
                          <button
                            onClick={() => setFilterType('authenticated')}
                            className={`px-4 py-2 rounded-lg transition-colors cursor-pointer whitespace-nowrap ${
                              filterType === 'authenticated'
                                ? 'bg-blue-600 text-white'
                                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                            }`}
                          >
                            Authenticated
                          </button>
                          <button
                            onClick={() => setFilterType('anonymous')}
                            className={`px-4 py-2 rounded-lg transition-colors cursor-pointer whitespace-nowrap ${
                              filterType === 'anonymous'
                                ? 'bg-blue-600 text-white'
                                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                            }`}
                          >
                            Anonymous
                          </button>
                        </div>

                        <div className="flex gap-2">
                          <button
                            onClick={() => setSortBy('recent')}
                            className={`px-4 py-2 rounded-lg transition-colors cursor-pointer whitespace-nowrap ${
                              sortBy === 'recent'
                                ? 'bg-purple-600 text-white'
                                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                            }`}
                          >
                            <i className="ri-time-line mr-2"></i>
                            Most Recent
                          </button>
                          <button
                            onClick={() => setSortBy('frequent')}
                            className={`px-4 py-2 rounded-lg transition-colors cursor-pointer whitespace-nowrap ${
                              sortBy === 'frequent'
                                ? 'bg-purple-600 text-white'
                                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                            }`}
                          >
                            <i className="ri-fire-line mr-2"></i>
                            Most Frequent
                          </button>
                        </div>

                        <button
                          onClick={exportToCSV}
                          className="ml-auto px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors cursor-pointer whitespace-nowrap"
                        >
                          <i className="ri-download-line mr-2"></i>
                          Export CSV
                        </button>
                      </div>

                      {/* Visitor Logs Table */}
                      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
                        <div className="overflow-x-auto">
                          <table className="w-full">
                            <thead className="bg-gray-50 border-b border-gray-200">
                              <tr>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                  Visitor
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                  Email
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                  Visits
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                  First Visit
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                  Last Visit
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                  Browser
                                </th>
                              </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                              {filteredAndSortedLogs.length === 0 ? (
                                <tr>
                                  <td colSpan={6} className="px-6 py-12 text-center">
                                    <i className="ri-inbox-line text-4xl text-gray-400 mb-2"></i>
                                    <p className="text-gray-600">No visitor logs found</p>
                                  </td>
                                </tr>
                              ) : (
                                filteredAndSortedLogs.map((log) => (
                                  <tr key={log.id} className="hover:bg-gray-50 transition-colors">
                                    <td className="px-6 py-4 whitespace-nowrap">
                                      <div className="flex items-center">
                                        <div className={`w-10 h-10 flex items-center justify-center rounded-full mr-3 ${
                                          log.email ? 'bg-green-100' : 'bg-gray-100'
                                        }`}>
                                          <i className={`${
                                            log.email ? 'ri-user-line text-green-600' : 'ri-user-unfollow-line text-gray-400'
                                          } text-lg`}></i>
                                        </div>
                                        <div className="text-sm font-mono text-gray-600">
                                          {log.visitor_id.substring(0, 8)}...
                                        </div>
                                      </div>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                      {log.email ? (
                                        <span className="text-sm text-gray-900">{log.email}</span>
                                      ) : (
                                        <span className="text-sm text-gray-400 italic">Anonymous</span>
                                      )}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                        {log.visit_count} {log.visit_count === 1 ? 'visit' : 'visits'}
                                      </span>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                                      {formatDate(log.first_visit)}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                                      {formatDate(log.last_visit)}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                                      {log.user_agent || 'Unknown'}
                                    </td>
                                  </tr>
                                ))
                              )}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </>
                  ) : (
                    <>
                      {/* Export Button for Users Tab */}
                      <div className="flex justify-end mb-6">
                        <button
                          onClick={exportToCSV}
                          className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors cursor-pointer whitespace-nowrap"
                        >
                          <i className="ri-download-line mr-2"></i>
                          Export CSV
                        </button>
                      </div>

                      {/* Registered Users Table */}
                      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
                        <div className="overflow-x-auto">
                          <table className="w-full">
                            <thead className="bg-gray-50 border-b border-gray-200">
                              <tr>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                  Email
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                  Name
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                  Registered Date
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                  Last Sign In
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                  Visit Count
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                  Last Visit
                                </th>
                              </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                              {registeredUsers.length === 0 ? (
                                <tr>
                                  <td colSpan={6} className="px-6 py-12 text-center">
                                    <i className="ri-user-add-line text-4xl text-gray-400 mb-2"></i>
                                    <p className="text-gray-600">No registered users found</p>
                                  </td>
                                </tr>
                              ) : (
                                registeredUsers.map((user) => (
                                  <tr key={user.id} className="hover:bg-gray-50 transition-colors">
                                    <td className="px-6 py-4 whitespace-nowrap">
                                      <div className="flex items-center">
                                        <div className="w-10 h-10 flex items-center justify-center bg-blue-100 rounded-full mr-3">
                                          <i className="ri-mail-line text-blue-600 text-lg"></i>
                                        </div>
                                        <span className="text-sm font-medium text-gray-900">{user.email}</span>
                                      </div>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                                      {user.raw_user_meta_data?.full_name || user.raw_user_meta_data?.name || (
                                        <span className="text-gray-400 italic">Not provided</span>
                                      )}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                                      {formatDate(user.created_at)}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                                      {user.last_sign_in_at ? (
                                        formatDate(user.last_sign_in_at)
                                      ) : (
                                        <span className="text-gray-400 italic">Never</span>
                                      )}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                                        {user.visit_count || 0} {user.visit_count === 1 ? 'visit' : 'visits'}
                                      </span>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                                      {user.last_visit ? (
                                        formatDate(user.last_visit)
                                      ) : (
                                        <span className="text-gray-400 italic">Never</span>
                                      )}
                                    </td>
                                  </tr>
                                ))
                              )}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
